import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import {
  TreePine,
  Sparkles,
  Trophy,
  Heart,
  Ruler,
  CalendarCheck,
  ShieldCheck,
  Download,
  ArrowLeft,
  ArrowRight,
  Phone,
  Mail,
  Users,
  Gift,
  PlayCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Path to the current guidelines PDF. Swap this to the 2026 doc once the
// updated version is ready — the download button consumes this URL directly.
const GUIDELINES_PDF_URL =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_7adffd06-a6e7-4f95-8888-9b22d8068cd7/artifacts/03cbd81ede64e3af_2026%20Festival%20of%20Trees%20Guidelines.pdf";
const GUIDELINES_YEAR = "2026"; // update to "2026" when the new PDF replaces the link above

// Promo video for the 2026 Festival — replace this URL when a fresher edit is available.
const PROMO_VIDEO_URL =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_7adffd06-a6e7-4f95-8888-9b22d8068cd7/artifacts/j6l35d7b_Holiday_Festival_of_Trees_2026_promo_v5%20%282%29.mp4";

const HOW_IT_WORKS = [
  {
    icon: Sparkles,
    title: "Dream up your theme",
    body: "Choose a family-friendly theme that celebrates the season, our first-responder honorees, or the many faiths in our Loudoun community.",
  },
  {
    icon: TreePine,
    title: "Deck it out",
    body: "Decorate a brand-new artificial tree (3–6 ft), secured with wire, zip ties, or glue. Add gifts, cards, or accessories that make it a full experience.",
  },
  {
    icon: CalendarCheck,
    title: "Deliver by Thursday, Nov 19",
    body: "Drop your finished tree at The Barn at Brambleton by 3:00 pm on Thursday, November 19, 2026. We'll stage it beautifully with signage bearing your theme name.",
  },
  {
    icon: Heart,
    title: "Watch it find a home",
    body: "Shoppers spend two days dropping raffle tickets into the tree they want to win. Every dollar goes to the Loudoun First Responders Foundation.",
  },
];

const PRIZES = [
  {
    icon: Users,
    label: "Most Popular",
    body: "Awarded to the tree that collects the most raffle tickets over the weekend.",
  },
  {
    icon: Sparkles,
    label: "Most Beautiful",
    body: "Chosen by the AMS committee for craftsmanship, composition, and visual delight.",
  },
  {
    icon: Trophy,
    label: "Best Theme",
    body: "Given to the tree whose execution most fully commits to a coherent, memorable idea.",
  },
];

const GUIDELINE_CHIPS = [
  { icon: CalendarCheck, text: "Tree drop-off: Thursday, November 19, 2026 by 3:00 pm at The Barn at Brambleton." },
  { icon: Ruler, text: "3 ft minimum, 6 ft maximum height. Tree must fill at least 60% of a 4' × 4' footprint." },
  { icon: TreePine, text: "New, artificial trees only — never previously used." },
  { icon: ShieldCheck, text: "All decorations secured (florist wire, zip ties, or glue). No open hooks." },
  { icon: Sparkles, text: "Lights & animations must work; sponsor supplies fresh batteries. Power provided on-site." },
  { icon: Heart, text: "Family-friendly themes only. All proceeds benefit Loudoun First Responders." },
];

export default function FestivalOfTreesDonatePage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    business_name: "",
    business_website: "",
    theme_name: "",
    tree_description: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.theme_name.trim() || !form.tree_description.trim()) {
      toast.error("Please fill in your name, email, theme, and tree description.");
      return;
    }
    setSubmitting(true);
    // Package tree-specific fields into the message so the admin sees the full picture.
    const parts = [
      `PROPOSED THEME: ${form.theme_name}`,
      "",
      "TREE DESCRIPTION:",
      form.tree_description,
    ];
    if (form.business_website.trim()) {
      parts.push("", `WEBSITE: ${form.business_website}`);
    }
    const message = parts.join("\n");

    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone || null,
          business_name: form.business_name || null,
          subject: `Festival of Trees — tree sponsor: "${form.theme_name}"`,
          message,
          event_context: "festival-of-trees-donation",
        }),
      });
      if (!res.ok) throw new Error("send failed");
      setSubmitted(true);
      toast.success("Thank you — your tree submission is in. Kathy will be in touch.");
    } catch {
      toast.error("Something went wrong. Please try again or email info@artisanmarketandstudio.com.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="editorial min-h-screen" data-testid="fot-donate-page">
      <Helmet>
        <title>Sponsor a Tree — Festival of Trees | Artisan Market &amp; Studio</title>
        <meta
          name="description"
          content="Sponsor a fully decorated tree at the Barn at Brambleton Festival of Trees. All raffle proceeds benefit the Loudoun First Responders Foundation."
        />
      </Helmet>

      {/* Hero */}
      <section className="relative overflow-hidden bg-[color:var(--ed-forest-dark)] text-[#FDF7EA]">
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1512389142860-9c449e58a543?w=1800&q=80')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--ed-forest-dark)] via-[color:var(--ed-forest-dark)]/85 to-[color:var(--ed-forest-dark)]/50" aria-hidden="true" />
        <div className="relative max-w-5xl mx-auto px-6 pt-24 pb-24 md:pt-32 md:pb-32">
          <Link
            to="/events/festival-of-trees"
            className="inline-flex items-center gap-2 text-sm font-body opacity-80 hover:opacity-100 mb-10"
            data-testid="fot-donate-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> Back to the Festival
          </Link>
          <span className="kicker block text-[color:var(--ed-gold)]" data-testid="fot-donate-kicker">
            A call for trees · 4th annual
          </span>
          <h1
            className="mt-4 font-editorial text-5xl md:text-7xl leading-[1.02] max-w-3xl"
            data-testid="fot-donate-title"
          >
            Sponsor a tree.
            <br />
            Light up a family&apos;s holiday.
          </h1>
          <p className="mt-6 max-w-2xl font-body text-lg text-[#FDF7EA]/90">
            The Barn at Brambleton Festival of Trees returns — and we&apos;re calling on artists,
            families, faith groups, and small businesses across the DMV to donate a fully decorated
            tree. Every raffle dollar goes to the Loudoun First Responders Foundation.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href="#donate-form"
              className="btn-primary"
              data-testid="fot-donate-cta-primary"
            >
              Submit your tree
              <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href={GUIDELINES_PDF_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#FDF7EA]/40 text-[#FDF7EA] font-body text-sm hover:bg-[#FDF7EA]/10 transition-colors"
              data-testid="fot-donate-guidelines-hero"
            >
              <Download className="w-4 h-4" />
              Read the {GUIDELINES_YEAR} guidelines
            </a>
          </div>

          {/* Partner attribution — every raffle dollar goes here. Logo on a light card
              so it reads clearly against the dark forest hero. */}
          <div
            className="mt-12 inline-flex flex-col sm:flex-row items-center gap-5 p-4 sm:p-5 rounded-2xl bg-[#FDF7EA] shadow-lg max-w-md"
            data-testid="fot-donate-lfrf-partner"
          >
            <a
              href="https://lfrf.org/"
              target="_blank"
              rel="noopener noreferrer"
              className="block flex-shrink-0"
              aria-label="Loudoun First Responders Foundation"
              data-testid="fot-donate-lfrf-logo-link"
            >
              <img
                src="/lfrf-logo.jpg"
                alt="Loudoun First Responders Foundation"
                className="h-14 sm:h-16 w-auto object-contain"
                data-testid="fot-donate-lfrf-logo"
              />
            </a>
            <div className="text-center sm:text-left">
              <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[color:var(--ed-terracotta-dark,#8F3E22)]">
                All proceeds benefit
              </div>
              <div className="mt-1 font-editorial text-lg leading-tight text-[color:var(--ed-forest-dark)]">
                Loudoun First Responders Foundation
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Promo video — sits directly under the hero so it's the second thing
          every visitor experiences. Portrait 9:16 source so we constrain it to
          a phone-shaped card so the page never scrolls just to fit the video.
          Autoplays muted (works on iOS/Android), loops softly; controls surface
          on hover for sound. */}
      <section
        className="max-w-6xl mx-auto px-6 pt-14 pb-8 relative z-10"
        data-testid="fot-donate-video-section"
      >
        <div className="flex items-center justify-center mb-6">
          <span className="rule" />
          <span className="kicker inline-flex items-center gap-1.5">
            <PlayCircle className="w-3.5 h-3.5" />
            Watch the 2026 promo
          </span>
          <span className="rule" />
        </div>
        <div className="flex flex-col md:flex-row items-center md:items-start justify-center gap-8 md:gap-12">
          <div
            className="relative w-full max-w-[300px] rounded-2xl overflow-hidden shadow-xl ring-1 ring-[color:var(--ed-forest)]/15 bg-black"
            style={{ aspectRatio: "9 / 16" }}
            data-testid="fot-donate-video-frame"
          >
            <video
              src={PROMO_VIDEO_URL}
              poster="/festival-of-trees-poster.jpg"
              className="absolute inset-0 w-full h-full object-cover"
              autoPlay
              muted
              loop
              playsInline
              controls
              preload="metadata"
              data-testid="fot-donate-video"
            >
              Your browser doesn&apos;t support embedded video. You can{" "}
              <a href={PROMO_VIDEO_URL} target="_blank" rel="noopener noreferrer">
                download the promo here
              </a>
              .
            </video>
          </div>
          <div className="max-w-sm text-center md:text-left">
            <h3 className="font-editorial text-3xl md:text-4xl leading-tight text-[color:var(--ed-forest-dark)]">
              A one-minute look at last year&apos;s Festival.
            </h3>
            <p className="mt-4 font-body text-base text-[color:var(--ed-muted)]">
              The trees, the crowd, the first responders who benefit. Tap the
              speaker to hear the room — it&apos;s better with sound.
            </p>
          </div>
        </div>
      </section>

      {/* Intro / mission ribbon */}
      <section className="max-w-4xl mx-auto px-6 pt-20 pb-8">
        <div className="flex items-center mb-6">
          <span className="rule" />
          <span className="kicker">Why donate</span>
        </div>
        <p className="font-editorial text-3xl md:text-4xl leading-snug text-[color:var(--ed-forest-dark)]">
          &ldquo;Your donation of time, talent, and artistic display will bring a lucky raffle
          winner many happy years with a beautifully decorated tree.&rdquo;
        </p>
        <p className="mt-6 font-body text-base text-[color:var(--ed-muted)] max-w-2xl">
          The Festival is a fixture in our Loudoun County community — a weekend where local
          makers, businesses, and families come together to raise real money for the first
          responders who protect us. Sponsoring a tree is a chance to advertise, to create, and to
          give — all at once.
        </p>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="flex items-center mb-10">
          <span className="rule" />
          <span className="kicker">How it works</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="fot-donate-steps">
          {HOW_IT_WORKS.map((step, i) => {
            const Icon = step.icon;
            return (
              <div
                key={step.title}
                className="card-warm p-6"
                data-testid={`fot-donate-step-${i + 1}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[color:var(--ed-terracotta)]/10 text-[color:var(--ed-terracotta)]">
                    <Icon className="w-5 h-5" />
                  </span>
                  <span className="text-xs font-body uppercase tracking-widest text-[color:var(--ed-muted)]">
                    Step {i + 1}
                  </span>
                </div>
                <h3 className="font-editorial text-2xl text-[color:var(--ed-forest-dark)]">
                  {step.title}
                </h3>
                <p className="mt-2 font-body text-sm text-[color:var(--ed-muted)] leading-relaxed">
                  {step.body}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Benefits for sponsors */}
      <section className="bg-[color:var(--ed-cream)]/60 border-y border-[color:var(--ed-forest)]/10">
        <div className="max-w-6xl mx-auto px-6 py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="flex items-center mb-6">
              <span className="rule" />
              <span className="kicker">What sponsors get</span>
            </div>
            <h2 className="font-editorial text-4xl md:text-5xl leading-tight text-[color:var(--ed-forest-dark)]">
              Community reach, creative bragging rights, and a great cause.
            </h2>
            <p className="mt-6 font-body text-base text-[color:var(--ed-muted)] max-w-xl">
              Sponsors submitting on behalf of a business are welcome to attach a small
              (6&quot; max) advertising card to their tree — <em>&ldquo;This tree proudly
              sponsored by ______&rdquo;</em>. Between raffle exposure and prize eligibility,
              this is one of the most visible ways to introduce your brand to Loudoun families
              during the holidays.
            </p>
          </div>
          <ul className="space-y-4" data-testid="fot-donate-prizes">
            {PRIZES.map((p) => {
              const Icon = p.icon;
              return (
                <li
                  key={p.label}
                  className="flex items-start gap-4 p-5 rounded-2xl bg-white border border-[color:var(--ed-forest)]/10"
                  data-testid={`fot-donate-prize-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[color:var(--ed-gold)]/15 text-[color:var(--ed-gold-dark, #8a6a1f)] flex-shrink-0">
                    <Icon className="w-5 h-5" />
                  </span>
                  <div>
                    <div className="text-xs font-body uppercase tracking-widest text-[color:var(--ed-muted)]">
                      Prize category
                    </div>
                    <h3 className="mt-1 font-editorial text-2xl text-[color:var(--ed-forest-dark)]">
                      {p.label}
                    </h3>
                    <p className="mt-1 font-body text-sm text-[color:var(--ed-muted)]">
                      {p.body}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* Guidelines snapshot */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="flex items-center mb-6">
          <span className="rule" />
          <span className="kicker">The essentials</span>
        </div>
        <h2 className="font-editorial text-4xl md:text-5xl leading-tight text-[color:var(--ed-forest-dark)]">
          Six things every sponsor needs to know.
        </h2>
        <p className="mt-4 font-body text-[color:var(--ed-muted)] max-w-2xl">
          A snapshot of the full guidelines — please read the {GUIDELINES_YEAR} PDF before
          submitting your tree to make sure your entry stays eligible.
        </p>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="fot-donate-guidelines-chips">
          {GUIDELINE_CHIPS.map((chip, i) => {
            const Icon = chip.icon;
            return (
              <div
                key={i}
                className="flex items-start gap-3 p-4 rounded-xl border border-[color:var(--ed-forest)]/12 bg-white"
              >
                <Icon className="w-5 h-5 mt-0.5 text-[color:var(--ed-terracotta)] flex-shrink-0" />
                <p className="font-body text-sm text-[color:var(--ed-forest-dark)] leading-relaxed">
                  {chip.text}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Guidelines PDF download slot */}
      <section className="max-w-4xl mx-auto px-6 pb-16" data-testid="fot-donate-guidelines-slot">
        <div className="card-warm p-8 md:p-10 bg-[color:var(--ed-forest-dark)] text-[#FDF7EA]">
          <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
            <div className="flex-1">
              <div className="text-xs font-body uppercase tracking-widest text-[color:var(--ed-gold)]">
                Sponsor guidelines · {GUIDELINES_YEAR}
              </div>
              <h3 className="mt-2 font-editorial text-3xl md:text-4xl">
                Download the full artist guidelines.
              </h3>
              <p className="mt-3 font-body text-sm text-[#FDF7EA]/85 max-w-xl">
                Everything a sponsor needs: tree specs, decoration rules, drop-off timing
                (Thursday, Nov 19 by 3:00 pm), prize categories, and the full participation
                agreement. Read before you build.
              </p>
            </div>
            <a
              href={GUIDELINES_PDF_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[color:var(--ed-terracotta)] hover:bg-[color:var(--ed-terracotta)]/90 text-white font-body text-sm transition-colors flex-shrink-0"
              data-testid="fot-donate-guidelines-download"
            >
              <Download className="w-4 h-4" />
              Download PDF ({GUIDELINES_YEAR})
            </a>
          </div>
        </div>
      </section>

      {/* Coordinator */}
      <section className="max-w-4xl mx-auto px-6 pb-16" data-testid="fot-donate-coordinator">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 p-6 rounded-2xl bg-white border border-[color:var(--ed-forest)]/10">
          <div>
            <div className="text-xs font-body uppercase tracking-widest text-[color:var(--ed-muted)]">
              Your event coordinator
            </div>
            <h3 className="mt-1 font-editorial text-2xl text-[color:var(--ed-forest-dark)]">
              Kathy Copeland
            </h3>
            <p className="mt-1 font-body text-sm text-[color:var(--ed-muted)]">
              Handles special delivery arrangements outside the standard check-in window.
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <a
              href="tel:2146003004"
              className="inline-flex items-center gap-2 font-body text-sm text-[color:var(--ed-forest-dark)] hover:text-[color:var(--ed-terracotta)]"
            >
              <Phone className="w-4 h-4" /> (214) 600-3004
            </a>
            <a
              href="mailto:kathy@mendedheartglass.com"
              className="inline-flex items-center gap-2 font-body text-sm text-[color:var(--ed-forest-dark)] hover:text-[color:var(--ed-terracotta)]"
            >
              <Mail className="w-4 h-4" /> kathy@mendedheartglass.com
            </a>
          </div>
        </div>
      </section>

      {/* Signup form */}
      <section id="donate-form" className="max-w-3xl mx-auto px-6 pb-32">
        <div className="card-warm p-8 md:p-10" data-testid="fot-donate-form-card">
          <div className="flex items-center mb-4">
            <span className="rule" />
            <span className="kicker">Submit your tree</span>
          </div>
          <h2 className="font-editorial text-4xl md:text-5xl leading-tight text-[color:var(--ed-forest-dark)]">
            Tell us about your tree.
          </h2>
          <p className="mt-3 font-body text-[color:var(--ed-muted)]">
            The first 20 compliant submissions are accepted. Fill in the details below and
            we&apos;ll confirm your spot by email.
          </p>

          {submitted ? (
            <div
              className="mt-8 p-6 rounded-2xl bg-[color:var(--ed-gold)]/10 border border-[color:var(--ed-gold)]/40"
              data-testid="fot-donate-success"
            >
              <h3 className="font-editorial text-2xl text-[color:var(--ed-forest-dark)]">
                You&apos;re in the queue.
              </h3>
              <p className="mt-2 font-body text-sm text-[color:var(--ed-muted)]">
                Thank you for your submission. Kathy or the AMS team will confirm your spot and
                share check-in logistics by email shortly.
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="fot-donate-form">
              <Field
                label="Your name *"
                value={form.name}
                onChange={onChange("name")}
                testid="fot-donate-input-name"
                required
              />
              <Field
                label="Email *"
                type="email"
                value={form.email}
                onChange={onChange("email")}
                testid="fot-donate-input-email"
                required
              />
              <Field
                label="Phone"
                type="tel"
                value={form.phone}
                onChange={onChange("phone")}
                testid="fot-donate-input-phone"
              />
              <Field
                label="Business or group name"
                value={form.business_name}
                onChange={onChange("business_name")}
                testid="fot-donate-input-business"
              />
              <Field
                label="Business website"
                type="url"
                placeholder="https://…"
                value={form.business_website}
                onChange={onChange("business_website")}
                testid="fot-donate-input-website"
                fullWidth
              />
              <Field
                label="Proposed theme name *"
                placeholder='e.g. "Candy Cane Kitchen"'
                value={form.theme_name}
                onChange={onChange("theme_name")}
                testid="fot-donate-input-theme"
                required
                fullWidth
              />
              <div className="md:col-span-2">
                <label className="block text-xs font-body uppercase tracking-widest text-[color:var(--ed-muted)] mb-2">
                  Describe your tree *
                </label>
                <textarea
                  className="admin-input w-full min-h-[140px] resize-y"
                  placeholder="Colors, decorations, the story behind your theme, any accessories or gifts on/around the tree…"
                  value={form.tree_description}
                  onChange={onChange("tree_description")}
                  required
                  data-testid="fot-donate-input-description"
                />
              </div>
              <div className="md:col-span-2 flex items-center justify-between gap-4 pt-2">
                <p className="font-body text-xs text-[color:var(--ed-muted)] max-w-md">
                  By submitting, you confirm you&apos;ve read the {GUIDELINES_YEAR} guidelines
                  and understand that any tree submitted becomes property of the event host.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="fot-donate-submit"
                >
                  {submitting ? "Submitting…" : "Submit tree"}
                  {!submitting && <ArrowRight className="w-4 h-4" />}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text", required, placeholder, fullWidth }) {
  return (
    <div className={fullWidth ? "md:col-span-2" : ""}>
      <label className="block text-xs font-body uppercase tracking-widest text-[color:var(--ed-muted)] mb-2">
        {label}
      </label>
      <input
        type={type}
        className="admin-input w-full"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        data-testid={testid}
      />
    </div>
  );
}
